/**
 * Shape Type Mapper Utility
 *
 * This utility provides consistent shape type mapping between different naming conventions
 * used across the canvas system. It ensures that shape types are normalized to a
 * consistent internal format regardless of how they're stored or received.
 */

// Internal canonical types (PascalCase)
export const CANONICAL_TYPES = {
  TEXT: "Text",
  RECTANGLE: "Rect",
  CIRCLE: "Circle",
  ARROW: "Arrow",
  DOUBLE_SIDED_ARROW: "DoubleSidedArrow",
  LINE: "Line",
  IMAGE: "Image",
  VIDEO: "Video",
  SCRIBBLE: "Scribble",
};

// Mapping from various input formats to canonical types
const TYPE_MAPPINGS = {
  // Text variations
  text: CANONICAL_TYPES.TEXT,
  Text: CANONICAL_TYPES.TEXT,
  TEXT: CANONICAL_TYPES.TEXT,

  // Rectangle variations
  rectangle: CANONICAL_TYPES.RECTANGLE,
  rect: CANONICAL_TYPES.RECTANGLE,
  Rect: CANONICAL_TYPES.RECTANGLE,
  Rectangle: CANONICAL_TYPES.RECTANGLE,
  RECTANGLE: CANONICAL_TYPES.RECTANGLE,

  // Circle variations
  circle: CANONICAL_TYPES.CIRCLE,
  Circle: CANONICAL_TYPES.CIRCLE,
  CIRCLE: CANONICAL_TYPES.CIRCLE,

  // Arrow variations
  arrow: CANONICAL_TYPES.ARROW,
  Arrow: CANONICAL_TYPES.ARROW,
  ARROW: CANONICAL_TYPES.ARROW,

  // Double-sided arrow variations
  double_sided_arrow: CANONICAL_TYPES.DOUBLE_SIDED_ARROW,
  doubleSidedArrow: CANONICAL_TYPES.DOUBLE_SIDED_ARROW,
  DoubleSidedArrow: CANONICAL_TYPES.DOUBLE_SIDED_ARROW,
  DOUBLE_SIDED_ARROW: CANONICAL_TYPES.DOUBLE_SIDED_ARROW,

  // Line variations
  line: CANONICAL_TYPES.LINE,
  lines: CANONICAL_TYPES.LINE,
  Line: CANONICAL_TYPES.LINE,
  Lines: CANONICAL_TYPES.LINE,
  LINE: CANONICAL_TYPES.LINE,
  LINES: CANONICAL_TYPES.LINE,

  // Image variations
  image: CANONICAL_TYPES.IMAGE,
  Image: CANONICAL_TYPES.IMAGE,
  IMAGE: CANONICAL_TYPES.IMAGE,
  img: CANONICAL_TYPES.IMAGE,
  Img: CANONICAL_TYPES.IMAGE,
  IMG: CANONICAL_TYPES.IMAGE,

  // Video variations
  video: CANONICAL_TYPES.VIDEO,
  Video: CANONICAL_TYPES.VIDEO,
  VIDEO: CANONICAL_TYPES.VIDEO,

  // Scribble variations
  scribble: CANONICAL_TYPES.SCRIBBLE,
  Scribble: CANONICAL_TYPES.SCRIBBLE,
  SCRIBBLE: CANONICAL_TYPES.SCRIBBLE,
};

/**
 * Layout / Lexical marker ids only. Do **not** include `line_*` client temp ids here:
 * those must participate in API whitelist merge like normal ids; otherwise deleted
 * arrows keep matching `isUserCreatedCanvasShape` and never drop from IndexedDB.
 * @param {string} id - id or shape_id to check
 * @returns {boolean}
 */
export const isMarkerId = (id) => {
  if (id == null || typeof id !== "string") return false;
  const s = id.trim();
  return s.startsWith("marker-") || s.includes("marker_");
};

/**
 * Client-generated line/arrow ids (not UUIDs). Omit as top-level `shape_id` on API save;
 * do not use for "preserve as marker layout" merge rules.
 */
export const isClientTempLineLikeId = (id) => {
  if (id == null || typeof id !== "string") return false;
  const s = id.trim();
  return (
    s.includes("line_") ||
    s.includes("arrow_") ||
    s.includes("double_arrow_")
  );
};

/**
 * Normalizes a shape type to the canonical internal format
 * @param {string} type - The input shape type
 * @returns {string} - The canonical shape type
 */
export const normalizeShapeType = (type) => {
  if (!type || typeof type !== "string") {
    return type;
  }

  return TYPE_MAPPINGS[type] || type;
};

/**
 * Normalizes an array of shapes to use canonical types
 * @param {Array} shapes - Array of shape objects
 * @returns {Array} - Array of shapes with normalized types
 */
export const normalizeShapes = (shapes) => {
  if (!Array.isArray(shapes)) {
    return shapes;
  }

  return shapes.map((shape) => ({
    ...shape,
    type: normalizeShapeType(shape.type),
  }));
};

/**
 * Normalizes a single shape object to use canonical type
 * @param {Object} shape - Shape object
 * @returns {Object} - Shape object with normalized type
 */
export const normalizeShape = (shape) => {
  if (!shape || typeof shape !== "object") {
    return shape;
  }

  return {
    ...shape,
    type: normalizeShapeType(shape.type),
  };
};

/**
 * Checks if a type matches any of the provided canonical types
 * @param {string} type - The type to check
 * @param {...string} canonicalTypes - Canonical types to match against
 * @returns {boolean} - True if type matches any of the canonical types
 */
export const isShapeType = (type, ...canonicalTypes) => {
  const normalizedType = normalizeShapeType(type);
  return canonicalTypes.includes(normalizedType);
};

/**
 * Gets the snake_case version of a canonical type (for API/storage)
 * @param {string} canonicalType - The canonical type
 * @returns {string} - The snake_case version
 */
export const toSnakeCase = (canonicalType) => {
  const reverseMappings = {
    [CANONICAL_TYPES.TEXT]: "text",
    [CANONICAL_TYPES.RECTANGLE]: "rectangle",
    [CANONICAL_TYPES.CIRCLE]: "circle",
    [CANONICAL_TYPES.ARROW]: "arrow",
    [CANONICAL_TYPES.DOUBLE_SIDED_ARROW]: "double_sided_arrow",
    [CANONICAL_TYPES.LINE]: "lines",
    [CANONICAL_TYPES.IMAGE]: "image",
    [CANONICAL_TYPES.VIDEO]: "video",
    [CANONICAL_TYPES.SCRIBBLE]: "scribble",
  };

  return reverseMappings[canonicalType] || canonicalType;
};

/**
 * Gets the PascalCase version of a type (for internal use)
 * @param {string} type - Any type format
 * @returns {string} - The PascalCase version
 */
export const toPascalCase = (type) => {
  return normalizeShapeType(type);
};
